import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import Noti from "@components/atoms/Noti";
import Editor from "@components/molecules/Editor";
import { useV2FAQsControllerCreateFaq, type V2FaqCreateRequestDto, type V2FaqCreateRequestDtoMainCategory, type V2FaqCreateRequestDtoMiddleCategory } from "@everon-it/api-client/contents-gw";
import { useCascadingCategory } from "@hooks/useCascadingCategory";
import { useFetchFaqCategoryOption } from "@hooks/useFetchFaqCategoryOption";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import useRouter from "@hooks/useRouter";
import useAuthStore from "@store/auth";
import { colors, typo } from "@styles/index";
import { CommonElementType, RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import { useFormik } from "formik";
import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { type FaqCommonContentsOptionType, FaqCreateData } from "./Data";

export const FaqNoti = <Noti iconSrc={ICONS.NOTI_ICON.LIGHT_BULB} text="중분류는 고객에게 노출됩니다." />;

const FaqCreate: React.FC = () => {
    // Props
    const HEADER_TITLE = "FAQ 등록";
    const CONTENT_TITLE = "FAQ 내용";

    // Data
    const { formInitialValues } = FaqCreateData;

    // Hooks
    const { goBack } = useRouter();
    const { showConfirmModal, hideModal } = useGlobalModal();
    const { showConfirmNoti } = useGlobalNoti();
    const { userInfo } = useAuthStore();

    // api-client mutation (V2)
    const { mutate: createMutate } = useV2FAQsControllerCreateFaq({
        mutation: {
            onSuccess: () => {
                showConfirmNoti({});
                goBack();
            },
        },
    });

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: values => {
            const body: V2FaqCreateRequestDto = {
                isVisible: values.isVisible,
                mainCategory: values.mainCategory as V2FaqCreateRequestDtoMainCategory,
                middleCategory: values.middleCategory as V2FaqCreateRequestDtoMiddleCategory,
                title: values.title,
                content: values.content,
                createdBy: userInfo?.name ?? "",
            };
            createMutate({ data: body });
        },
    });

    // V2: 대분류는 파라미터 없이 조회
    const { categoryOption: mainCategoryOption } = useFetchFaqCategoryOption();
    const { categoryOption: middleCategoryOption, setCategoryOption: setMiddleCategoryOption } = useFetchFaqCategoryOption(formik.values.mainCategory);

    // 상위 카테고리 변경 시 하위 카테고리 초기화
    useCascadingCategory({
        formik,
        mainCategoryField: "mainCategory",
        subCategoryField: "middleCategory",
        setSubCategoryOption: setMiddleCategoryOption,
        preserveInitialSubCategory: false,
    });

    // Functions
    const handleGoBackClick = () => {
        const { mainCategory, middleCategory, title, content } = formik.values;

        if (mainCategory || middleCategory || title || content) {
            showConfirmModal({
                title: `작성한 내용을 저장하지 않고\n 취소 하시겠어요?`,
                firstButtonText: "닫기",
                secondButtonText: "확인",
                onConfirm: () => {
                    hideModal();
                    goBack();
                },
            });
        } else {
            goBack();
        }
    };

    const handleCreateContent = () => {
        showConfirmModal({
            title: `등록하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "등록하기",
            onConfirm: () => {
                hideModal();
                formik.handleSubmit();
            },
        });
    };

    const createButtonValidate = useCallback(() => {
        const { mainCategory, middleCategory, title, content } = formik.values;
        return !(mainCategory && middleCategory && title && content);
    }, [formik.values]);

    // Options & Elements : 카테고리의 옵션 값은 API로 받아옵니다.
    const contentsOption: FaqCommonContentsOptionType[] = [
        {
            label: "노출 상태",
            name: "isVisible",
            type: CommonElementType.TOGGLE,
        },
        {
            label: "대분류",
            name: "mainCategory",
            type: CommonElementType.SELECT,
            placeholder: "대분류를 선택해주세요",
            options: mainCategoryOption,
        },
        {
            label: "중분류",
            name: "middleCategory",
            type: CommonElementType.SELECT,
            placeholder: "중분류를 선택해주세요",
            notice: FaqNoti,
            options: middleCategoryOption,
        },
        {
            label: "제목",
            name: "title",
            type: CommonElementType.INPUT,
            placeholder: "제목을 입력해주세요",
        },
    ];

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>

            <div>
                <ContentBox style={{ padding: 0 }}>
                    <ContentsHeader>{CONTENT_TITLE}</ContentsHeader>

                    <ContentsMainWrap>
                        {contentsOption.map(({ label, name, options, notice, ...res }, _) => {
                            return (
                                <React.Fragment key={`${_}_contents`}>
                                    <InputWrap>
                                        <LabelWrap>
                                            <LabelInnerWrap>
                                                <Label>{label || "LABEL"}</Label>
                                            </LabelInnerWrap>
                                        </LabelWrap>
                                        <InputContentsWrap noti={!!notice}>
                                            {RenderCommonElementByType({
                                                formik,
                                                name,
                                                options,
                                                ...res,
                                            })}
                                            {notice && notice}
                                        </InputContentsWrap>
                                    </InputWrap>
                                </React.Fragment>
                            );
                        })}

                        <Editor formik={formik} initData={formik.values.content} />
                    </ContentsMainWrap>
                </ContentBox>

                <FooterButtonWrap>
                    <LeftButtonWrap />
                    <RightButtonWrap>
                        <Button text="취소" buttonType="line_black" onClick={handleGoBackClick} />
                        <Button disabled={createButtonValidate()} text="등록하기" onClick={handleCreateContent} />
                    </RightButtonWrap>
                </FooterButtonWrap>
            </div>
        </ContentsWrap>
    );
};

export default FaqCreate;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const HeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const HeaderTitle = styled(MainHeaderTitle)`
    margin: 0;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const ContentTitle = styled(TableTitle)`
    margin: 0;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const ContentsHeader = styled(ContentTitle)`
    display: flex;
    justify-content: space-between;
`;

const ContentsMainWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 840px;
    margin: auto;
    padding: 52px 0 80px;
`;

const InputWrap = styled.div`
    display: flex;
    gap: 12px;
    min-height: 48px;
    width: 100%;
`;

const LabelWrap = styled.span`
    min-width: 200px;
    padding: 12px 0 12px;
`;

const LabelInnerWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
const Label = styled.p`
    color: ${colors.GRAY6};
    ${typo.HEADING_7};
`;

const InputContentsWrap = styled.div<{ noti?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;

    ${({ noti }) =>
        noti &&
        css`
            flex-direction: column;
            gap: 12px;
        `}
`;

const FooterButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
`;

const LeftButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

const RightButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;
